import {
  Checkbox,
  Collapse,
  List,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Box, SxProps } from '@mui/system';
import { useState } from 'react';
import {
  ChapterTopicMiniInfo,
  SubjectChapterMiniInfo,
  TopicBasicInfo,
} from '@protos/content_management/content.common.apis_pb';
import {
  getMediaBasePath,
  IconWrapper,
  ImageWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../reduxStore/reduxHooks';

const styles: IStyles = {
  card: {
    cursor: 'pointer',
    width: '100%',
    // height: { xs: pxToRem(80), md: pxTovW(140) },
    padding: 0,
    boxSizing: 'border-box',

    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    justifyContent: 'center',
    display: 'flex',
  },

  iconTextBoxContainer: {
    width: '100%',
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    alignItems: 'center',
  },
};

interface IconDetails {
  iconName: string;
  text: string;
}
interface IProps {
  selected: boolean;
  chapterInfo: SubjectChapterMiniInfo;
  selectedChapterList: SubjectChapterMiniInfo[];
  addHandler?: (chapter: SubjectChapterMiniInfo) => void;
  chapterEditHandler: (chapter: SubjectChapterMiniInfo) => void;
  variant: 'small' | 'medium' | 'large';
  rootStyle?: SxProps;
  deleteHandler?: (chapter: SubjectChapterMiniInfo) => void;
  allowCardClickDelete?: boolean;
}

export const FAChapterSelectionCard = (props: IProps) => {
  const {
    chapterInfo,
    addHandler,
    selected,
    rootStyle,
    variant,
    deleteHandler,
    chapterEditHandler,
    selectedChapterList,
    allowCardClickDelete,
  } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { chapter_topic_data } = useAppSelector((state) => state.assessment);

  // --------------------------------------------------------------

  const isTopicSelected = (topicId: number) =>
    selectedChapterList
      ?.find((c) => c.chapterId === chapterInfo.chapterId)
      ?.topics?.some((topic) => topic.topicId === topicId) ?? false;

  // --------------------------------------------------------------

  const toggleTopicSelection = (item: TopicBasicInfo, checked: boolean) => {
    let selectedChapter = selectedChapterList?.find(
      (c) => c.chapterId === chapterInfo.chapterId
    );
    let updatedTopics: TopicBasicInfo[] = selectedChapter?.topics || [];

    if (checked) {
      updatedTopics = [...updatedTopics, item];
    } else {
      updatedTopics = updatedTopics.filter(
        (topic) => topic.topicId !== item.topicId
      );
      if (updatedTopics.length === 0) {
        setIsOpen(false);
        deleteHandler?.(chapterInfo);
        return;
      }
    }

    chapterEditHandler(
      new SubjectChapterMiniInfo({
        ...selectedChapter,
        topics: updatedTopics,
      })
    );
  };

  // --------------------------------------------------------------

  return (
    <List
      sx={{
        ...styles.card,
        backgroundColor: selected ? '#F2F9FF' : '#FFFFFF',
        border: selected ? '1.5px solid #007CDC' : 'none',
        boxShadow: selected ? 'none' : '0px 0px 6px #00000029',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 0,
        opacity: 1,
      }}
      onClick={(e) => {
        if (selected) {
          setIsOpen(false);
          allowCardClickDelete && deleteHandler?.(chapterInfo);
        } else {
          addHandler?.(chapterInfo);
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          padding: { xs: pxToRem(10), md: pxTovW(10) },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: pxToRem(10),
          }}
        >
          <ImageWrapper
            name={'chapterImage'}
            type="png"
            parentFolder={'tempAssets'}
            styles={{
              height: { xs: pxToRem(68), md: pxTovW(121) },
              width: { xs: pxToRem(68), md: pxTovW(121) },
              objectFit: 'cover',
              borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
            }}
            path={getMediaBasePath(
              chapterInfo.posterImagesUrl,
              'processedMediaBucket'
            )}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              // backgroundColor: 'blue',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                gap: pxToRem(7.5),
                flexDirection: 'column',
                //   backgroundColor: 'red',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  width: {
                    xs: pxToRem(150),
                    md: pxTovW(280),
                    lg: pxTovW(230),
                  },
                  wordBreak: 'break-word',
                }}
              >
                {chapterInfo.chapterNo
                  ? `${chapterInfo.chapterNo}.${chapterInfo.chapterTitle}`
                  : chapterInfo.chapterTitle}
              </Typography>

              {(chapter_topic_data?.chapters?.find(
                (c) => c.chapterId === chapterInfo.chapterId
              )?.topics?.length ?? 0) > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: pxToRem(5),
                  }}
                >
                  {selected ? (
                    <Typography
                      variant={isIpadOnly ? 'h4' : 'h5'}
                      color="#007CDC"
                    >
                      {`${
                        selectedChapterList?.find(
                          (c) => c.chapterId === chapterInfo.chapterId
                        )?.topics?.length
                      }/${
                        chapter_topic_data?.chapters?.find(
                          (c) => c.chapterId === chapterInfo.chapterId
                        )?.topics?.length
                      } topics`}
                    </Typography>
                  ) : (
                    <Typography
                      variant={isIpadOnly ? 'h4' : 'h5'}
                      color="#007CDC"
                    >
                      {`${chapterInfo.topics.length} topics`}
                    </Typography>
                  )}
                  {selected && (
                    <Box
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        event?.stopPropagation();
                        setIsOpen(!isOpen);
                      }}
                    >
                      <IconWrapper
                        name="edit"
                        size="md"
                        type="png"
                        parentFolder="icons"
                        customSx={{
                          border: '1px solid #0AA34F',
                          borderRadius: { xs: pxToRem(3), md: pxTovW(5) },
                          width: {
                            xs: pxToRem(14),
                            md: pxTovW(35),
                            lg: pxTovW(23),
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        sx={{ width: '100%', mt: pxTovW(5) }}
        onClick={(e) => e.stopPropagation()}
      >
        <List
          component="div"
          disablePadding
          sx={{
            height: 'max-content',
            maxHeight: { xs: 'max-content', md: pxTovW(450), lg: pxTovW(290) },
            overflowY: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          {chapter_topic_data?.chapters
            ?.find((c) => c.chapterId === chapterInfo.chapterId)
            ?.topics?.map((item: TopicBasicInfo, i: number) => (
              <Box
                key={item.topicId}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTopicSelection(item, !isTopicSelected(item.topicId));
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  height: {
                    xs: `calc(${variantMapping[variant]?.height.xs}-${pxToRem(
                      10
                    )})`,
                    md: `calc(${variantMapping[variant]?.height.md}-${pxTovW(
                      5
                    )})`,
                  },
                  padding: '0px 10px',
                  boxSizing: 'border-box',
                  alignItems: 'center',
                  borderTop: '1px solid #B5D9F6',
                  borderBottomLeftRadius:
                    i === chapterInfo.topics.length - 1 ? '10px' : '0px',
                  borderBottomRightRadius:
                    i === chapterInfo.topics.length - 1 ? '10px' : '0px',
                  paddingRight: '63px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#E8F4FF' },
                }}
              >
                <Box
                  sx={{
                    width: { xs: '200px', md: '220px', lg: pxTovW(366) },
                    height: { xs: '30px', md: '25px' },
                    display: 'flex',
                    gap: pxTovW(10),
                    alignItems: 'center',
                    paddingY: pxTovW(10),
                  }}
                >
                  <Checkbox
                    sx={{ height: '24px', width: '24px' }}
                    value={item.topicId}
                    checked={isTopicSelected(item.topicId)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      toggleTopicSelection(item, e.target.checked);
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.topicTitle}
                  </Typography>
                </Box>
              </Box>
            ))}
        </List>
      </Collapse>

      <IconWrapper
        name={selected ? 'deletehw' : 'addHw'}
        size="md"
        type="png"
        parentFolder="icons"
        onClick={() => {
          if (selected) {
            setIsOpen(false);
            deleteHandler?.(chapterInfo);
          }
        }}
        customSx={{
          width: { xs: pxToRem(17), md: pxTovW(35), lg: pxTovW(24) },
          margin: { xs: pxToRem(10), md: 0 },
          position: 'absolute',
          zIndex: 1,
          right: pxTovW(10),
          top: pxTovW(10),
        }}
      />
    </List>
  );
};

const variantMapping = {
  small: {
    height: { xs: pxToRem(25), md: pxTovW(30) },
  },
  medium: {
    height: { xs: pxToRem(35), md: pxTovW(35) },
  },

  large: {
    height: { xs: pxToRem(54), md: pxTovW(90) },
  },
};
